<template>
  <SettingsCard
    :title="$t('settings.crm.title')"
    :subtitle="$t('settings.crm.subtitle', { readablePlatformName })"
    :additional-info="additionalInfo"
    icon="mdi-account-sync-outline"
    :model-value="isOpen"
    @update:model-value="setOpen"
    :hide-header="slimMode"
  >
    <v-row class="mx-0 my-2 mx-md-2">
      <v-col
        v-if="!slimMode"
        cols="12"
        md="6"
      >
        <div class="font-weight-bold">
          {{ $t('settings.crm.selection-texts.0') }}
        </div>
        <div class="text-grey">
          <p>{{ $t('settings.crm.selection-texts.1') }}</p>
          <p>{{ $t('settings.crm.selection-texts.2', { readablePlatformName }) }}.</p>
        </div>
      </v-col>
      <v-col
        v-if="!slimMode"
        class="pt-2 pt-md-8"
        cols="12"
        offset-md="1"
        md="5"
      >
        <v-tooltip
          location="top"
          :disabled="!hasBeenActivated"
        >
          <template v-slot:activator="{ props }">
            <div
              class="w-full"
              v-bind="props"
            >
              <v-select
                v-model="crmProvider"
                :items="providers"
                :disabled="!!loadingPart || hasBeenActivated || isEditMode"
                :loading="loadingPart === 'crmProvider'"
                label="CRM"
                variant="outlined"
                required
                item-title="text"
                item-value="value"
                :item-props="(item) => ({ disabled: item.disabled })"
                hide-details
                v-bind="$attrs"
                @click="onClick('CRM', crmProvider)"
                @update:model-value="(value) => update({ crmProvider: value }, 'crmProvider')"
              >
                <template #item="{ props, item }">
                  <v-list-item
                    v-bind="props"
                    :disabled="item.raw.disabled"
                  >
                    <v-list-item-subtitle v-if="item.raw.disabled">
                      {{ $t(item.raw.inactiveReason) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-select>
            </div>
          </template>
          <span>{{ $t('settings.crm.help-texts.0') }}</span>
        </v-tooltip>
        <div class="mx-2">
          <span class="text-body-2">
            {{ $t('settings.crm.help-texts.1') }}
            <a
              :href="helpLink"
              target="_blank"
            >
              {{ $t('settings.crm.help-texts.2') }}
            </a>
          </span>
        </div>
      </v-col>
    </v-row>
    <component
      :is="credentialsComponent"
      v-if="crmProvider"
      :disabled="!!loadingPart || isActive || isEditMode"
      @isVerified="(value) => (isConnectionVerified = value)"
      @isManagedByParent="(val) => (isPropstackMultiCompanyChild = val)"
      @teamId="(val) => (multiCompanyChildTeamId = val)"
    />
    <component
      :is="filterComponent"
      v-if="isConnectionVerified && !isPropstackMultiCompanyChild"
      id="CrmFilter"
      :disabled="!!loadingPart || isActive || isEditMode"
      :is-edit-mode="isEditMode"
      @isFilterSelected="setSelectedFilter"
    />
    <PropstackMultiCompanyChildSection
      v-if="isPropstackMultiCompanyChild && isConnectionVerified && multiCompanyChildTeamId"
      :team-id="multiCompanyChildTeamId"
      data-product-fruits="propstack-multi-company-child-section"
      @sync-activated="() => $apollo.queries.config.refetch()"
    />
    <v-row
      v-if="isPropstackMultiCompanyChild && isConnectionVerified && !multiCompanyChildTeamId"
      class="mx-0 my-2 mx-md-2"
    >
      <v-col
        cols="12"
        offset-md="7"
        md="5"
        class="pt-2"
      >
        <v-alert
          type="info"
          density="compact"
          variant="tonal"
          data-product-fruits="propstack-multi-company-child-no-team-alert"
        >
          {{ $t('settings.crm.multi-company-child.no-team-assigned') }}
        </v-alert>
      </v-col>
    </v-row>
    <SynchronisationModeSelection
      v-if="
        crmProvider && isConnectionVerified && isFilterSelected && !isPropstackMultiCompanyChild
      "
      :loading-part="loadingPart"
      :is-edit-mode="isEditMode"
      :sync-modus="syncModus"
      :crm-config="crmConfig"
      :has-parent-company="hasParentCompany"
      :enforce-master-connection="enforceMasterConnection"
      :provider="crmProvider"
      @get:parentCrmConfig="getParentCrmConfig({ updateFromChild: true })"
      @update:syncModus="update({ syncModus: $event }, 'selectMode')"
    />
    <v-row
      v-if="isConnectionVerified && isFilterSelected && !isPropstackMultiCompanyChild"
      class="mx-0 my-2 mx-md-2"
    >
      <v-col
        cols="12"
        md="6"
      >
        <div class="font-weight-bold">
          {{ $t('settings.crm.synch-texts.2') }}
        </div>
        <div class="text-grey">
          {{ $t('settings.crm.synch-texts.3') }}
        </div>
      </v-col>
      <v-col
        class="pt-2 pt-md-8 d-flex flex-column align-md-end"
        cols="12"
        offset-md="1"
        md="5"
      >
        <SynchronisationState
          class="mb-4"
          :is-active="isActive"
        />
        <ActivateSyncDialog
          v-if="!isActive"
          id="ActivateSyncDialog"
          :disabled="!!loadingPart || isEditMode"
          :loading="loadingPart === 'isActive'"
          :automatic-sync="automaticSync"
          @activate="() => update({ isActive: true }, 'isActive')"
        />
        <DeactivateSyncDialog
          v-else
          :disabled="!!loadingPart || isEditMode"
          :loading="loadingPart === 'isActive'"
          :automatic-sync="automaticSync"
          @deactivate="() => update({ isActive: false }, 'isActive')"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="!crmProvider"
      class="mx-0 my-2 mx-md-2"
    >
      <v-alert
        icon="mdi-lightbulb-on-20"
        prominent
        variant="tonal"
        type="info"
        class="w-full pl-8 my-4"
      >
        <p class="pl-6 ma-0 font-weight-bold">{{ $t('labels.our-tip') }}:</p>
        <p class="pl-6">
          {{ $t('settings.crm.info-texts.0') }}<br />
          {{ $t('settings.crm.info-texts.1', { platform: readablePlatformName }) }}<br />
          {{ $t('settings.crm.info-texts.2') }}<br />
          {{ $t('settings.crm.info-texts.3') }}
          <a href="/leads">{{ $t('settings.crm.info-texts.4') }}</a>
        </p>
      </v-alert>
    </v-row>
  </SettingsCard>
</template>

<script>
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import eventBus, { eventNames } from '@/lib/eventBus'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import brandingMixin from '@/mixins/branding'
import crmMixin from '@/mixins/crm'
import featureMixin from '@/mixins/feature'
import SynchronisationModeSelection from '@/modules/settings/crmSettings/SynchronisationModeSelection.vue'

import GET_PARENT_COMPANY_CRM_CONFIG from '../../../../src/queries/GetParentCrmCompanyConfig.gql'
import HAS_PARENT_COMPANY from '../../../../src/queries/HasParentCompany.gql'
import CasaoneCredentials from './casaone/CasaoneCredentials.vue'
import CasaoneFilter from './casaone/CasaoneFilter.vue'
import ActivateSyncDialog from './dialogs/ActivateSyncDialog.vue'
import DeactivateSyncDialog from './dialogs/DeactivateSyncDialog.vue'
import EmproCredentials from './empro/EmproCredentials.vue'
import EmproFilter from './empro/EmproFilter.vue'
import FlowfactCredentials from './flowfact/FlowfactCredentials.vue'
import FlowfactFilter from './flowfact/FlowfactFilter.vue'
import OnofficeCredentials from './onoffice/OnofficeCredentials.vue'
import OnofficeFilter from './onoffice/OnofficeFilter.vue'
import PropstackCredentials from './propstack/PropstackCredentials.vue'
import PropstackFilter from './propstack/PropstackFilter.vue'
import PropstackMultiCompanyChildSection from './propstack/PropstackMultiCompanyChildSection.vue'
import CREATE_OR_UPDATE_CRM_COMPANY_CONFIG from './queries/CreateOrUpdateCrmCompanyConfig.gql'
import SynchronisationState from './SynchronisationState.vue'

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'crm' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: {
    SynchronisationModeSelection,
    SettingsCard,
    PropstackCredentials,
    PropstackMultiCompanyChildSection,
    SynchronisationState,
    ActivateSyncDialog,
    DeactivateSyncDialog,
    PropstackFilter,
    OnofficeCredentials,
    OnofficeFilter,
    EmproFilter,
    EmproCredentials,
    FlowfactCredentials,
    FlowfactFilter,
    CasaoneCredentials,
    CasaoneFilter,
  },
  mixins: [featureMixin, brandingMixin, crmMixin],
  props: {
    hideHelp: {
      type: Boolean,
      default: false,
    },
    slimMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      crmProvider: null,
      isActive: false,
      hasBeenActivated: false,
      automaticSync: null,
      enforceMasterConnection: false,
      acceptedMasterConnection: null,
      isConnectionVerified: false,
      isFilterSelected: false,
      loadingPart: false,
      isHelpHidden: this.hideHelp,
      hasParentCompany: false,
      syncModus: '',
      isPropstackMultiCompanyChild: false,
      multiCompanyChildTeamId: null,
    }
  },
  computed: {
    additionalInfo() {
      return this.hasParentCompany &&
        this.enforceMasterConnection &&
        this.acceptedMasterConnection !== false &&
        this.isOpen
        ? this.$t('settings.crm.subtitle-edit-mode')
        : ''
    },
    isEditMode() {
      return (
        this.hasParentCompany &&
        !this.crmConfig?.crmProvider &&
        this.enforceMasterConnection &&
        this.isActive
      )
    },
    credentialsComponent() {
      return {
        PROPSTACK: 'PropstackCredentials',
        ONOFFICE: 'OnofficeCredentials',
        FLOWFACT: 'FlowfactCredentials',
        CASAONE: 'CasaoneCredentials',
        EMPRO: 'EmproCredentials',
      }[this.crmProvider]
    },
    filterComponent() {
      return {
        PROPSTACK: 'PropstackFilter',
        ONOFFICE: 'OnofficeFilter',
        FLOWFACT: 'FlowfactFilter',
        CASAONE: 'CasaoneFilter',
        EMPRO: 'EmproFilter',
      }[this.crmProvider]
    },
    currentLocale() {
      return this.$i18n?.locale || this.$i18n?.fallbackLocale
    },
    helpLink() {
      return (
        {
          CASAONE:
            this.currentLocale?.toLowerCase() === 'de'
              ? 'https://help.bottimmo.com/de/article/empro-crm-verbinden-coming-soon-copy'
              : 'https://help.bottimmo.com/en/article/connect-empro-crm-coming-soon-copy',
          PROPSTACK:
            'https://scribehow.com/shared/Propstack_mit_BOTTIMMO_synchronisieren__98hEYNQbQamsQVJV3no6RQ',
          ONOFFICE:
            'https://scribehow.com/shared/onOffice_mit_BOTTIMMO_verbinden__sUIm44idQpeHIDqdJnHIZA',
          FLOWFACT:
            'https://scribehow.com/shared/FLOWFACT_mit_BOTTIMMO_synchronisieren__HIuNi9KqT6GQKhBDZvF4jA',
          EMPRO: 'https://help.bottimmo.com/en/article/connect-propstack-crm-with-bottimmo',
        }[this.crmProvider] || 'https://hilfe.bottimmo.com/portal/de/kb/general/einstellungen/crm'
      )
    },
  },
  watch: {
    crmProvider(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.isPropstackMultiCompanyChild = false
        this.multiCompanyChildTeamId = null
      }
    },
    crmConfig: {
      deep: true,
      handler(val) {
        if (val) {
          this.updateData(val || {})
        }
      },
    },
    async isConnectionVerified(val) {
      if (val) {
        await this.$nextTick()
        return document.querySelector('#CrmFilter')?.scrollIntoView({ behavior: 'smooth' })
      }
    },
  },
  mounted() {
    this.updateData(this.crmConfig || {})
  },
  methods: {
    onClick(label, value) {
      this.$tracking.event('Settings', 'Clicked', label, value)
    },
    updateData(config) {
      this.crmProvider = config.crmProvider
      this.automaticSync = config.syncModus === 'AUTO'
      this.syncModus = config.syncModus
      this.isActive = config.isActive
      this.hasBeenActivated = config.hasBeenActivated
      this.enforceMasterConnection = config.enforceMasterConnection
      this.acceptedMasterConnection = config.acceptedMasterConnection
    },
    async update(values, loadingPart) {
      if (this.isEditMode) {
        return
      }

      this.loadingPart = loadingPart
      try {
        await new Promise((resolve) => setTimeout(resolve, 700))
        await this.$apollo.mutate({
          mutation: CREATE_OR_UPDATE_CRM_COMPANY_CONFIG,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              crmProvider: this.crmProvider,
              isActive: this.isActive,
              ...values,
            },
          },
        })
        this.$tracking.event(
          'Settings',
          'Clicked',
          'Create or Update CRM Company Config',
          values?.crmProvider || null
        )
        eventBus.$emit(eventNames.CRM_UPDATED)
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.crm.success'),
        })
      } catch (err) {
        await this.$apollo.queries.config.refetch()
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.crm.error'),
        })
      } finally {
        this.loadingPart = false
      }
    },
    setSelectedFilter(value) {
      if (this.isEditMode) {
        return
      }

      this.isFilterSelected = value
      this.$emit('isFilterSelected', value)
      setTimeout(() => {
        return document.querySelector('#ActivateSyncDialog')?.scrollIntoView({ behavior: 'smooth' })
      }, 500)
      this.$tracking.event('Settings', 'Clicked', 'CRM Selected Filter')
    },
    async getParentCrmConfig({ updateFromChild = false }) {
      try {
        const {
          data: { config },
        } = await this.$apollo.query({
          query: GET_PARENT_COMPANY_CRM_CONFIG,
        })

        if (config?.enforceMasterConnection && (updateFromChild || !this.crmConfig)) {
          // Update local child config to reflect parent config
          this.updateData(config)
        } else if (this.crmConfig) {
          // Update local child config - only enforceMasterConnection, to reflect parent
          this.enforceMasterConnection = config.enforceMasterConnection
        }
      } catch (error) {
        showSnackbarMessage('error', this.$t('alerts.settings.crm.parent-config.error'))
      }
    },
  },
  emits: ['isFilterSelected'],
  apollo: {
    hasParentCompany: {
      query: HAS_PARENT_COMPANY,
      update(data) {
        if (data?.hasParentCompany) {
          this.getParentCrmConfig({ updateFromChild: false })
        }
        return data?.hasParentCompany ?? false
      },
    },
  },
}
</script>
